import React from 'react';
import cs from 'classnames';
import { isEqual } from 'lodash';

import { isPaymentsDisabled } from 'features/accounts/paymentsDisabled';
import BriefModule from 'features/briefs/components/atoms/BriefModule';
import BriefProductLinks from 'features/briefs/components/atoms/BriefProductLinks';
import CheckboxDisplay from 'features/briefs/components/atoms/CheckboxDisplay';
import CenteredSpinner from 'features/ui/CenteredSpinner';
import { Box, Text } from 'shared/components/display';
import { DefinitionList, ListDetail } from 'shared/components/molecules/DefinitionList';
import Richtext, { RICHTEXT_DEFAULT_VALUE } from 'shared/components/Richtext';

import { intl } from 'shared/lib/intl';
import COLORS from 'shared/styles/colors';
import { Brief } from 'shared/typings/briefs';
import { formatCurrency } from 'shared/utilities/stringUtility';

import styles from './styles';

interface Props {
  brief: Brief;
  loading?: boolean;
  className?: string;
}

const CreatorBriefView: React.FC<Props> = ({ brief, loading = false, className = '' }) => {
  return (
    <Box css={styles} className={cs('creator-brief-view', className)}>
      {loading ? (
        <CenteredSpinner />
      ) : (
        <Box className="creator-brief-view__content">
          <DefinitionList labelColor={COLORS.NEUTRAL800}>
            <ListDetail
              text="Brief Name"
              labelClassName="creator-brief-view__heading"
              className="creator-brief-view__detail"
            >
              {brief.name}
            </ListDetail>
            <ListDetail
              text="Brief Overview"
              labelClassName="creator-brief-view__heading"
              className="creator-brief-view__detail"
              hide={isEqual(RICHTEXT_DEFAULT_VALUE, JSON.parse(brief.description))}
            >
              <Richtext value={JSON.parse(brief.description)} key={`${brief.id}-description`} readOnly />
            </ListDetail>
            <ListDetail
              text="Product / Service"
              labelClassName="creator-brief-view__heading"
              className="creator-brief-view__detail"
              hide={!brief?.website?.length && !brief.shippingRequired}
              showBottomSeparator
            >
              {brief?.website?.length ? (
                <BriefProductLinks website={brief.website} className="creator-brief-view__product-links" />
              ) : (
                <></>
              )}
              {brief.shippingRequired ? (
                <CheckboxDisplay className="creator-brief-view__shipping-required" text="Shipping Required" />
              ) : (
                <></>
              )}
            </ListDetail>
            {/* Payments-disabled (WITHIN) accounts pay creators via Wrapbook, so no brkfst payout is shown. */}
            {!isPaymentsDisabled(brief.account) && (
              <ListDetail
                text="Creator Payout"
                labelClassName="creator-brief-view__heading"
                className="creator-brief-view__detail"
                showBottomSeparator
              >
                <Text data-cy="creator-brief-view__payout">
                  {intl.formatMessage(
                    {
                      id: 'DETAIL_PERFORMANCE_PAYOUT',
                    },
                    {
                      payout: formatCurrency(brief.eliteCreatorPayout ? brief.eliteCreatorPayout : brief.creatorPayout),
                      creativeOnly: brief.creativeOnly,
                    },
                  )}
                </Text>
              </ListDetail>
            )}

            <ListDetail
              text="Creator Requirements"
              labelClassName="creator-brief-view__heading"
              className="creator-brief-view__detail"
              hide={isEqual(RICHTEXT_DEFAULT_VALUE, JSON.parse(brief.creatorRequirements))}
              showBottomSeparator
            >
              <Richtext
                value={JSON.parse(brief.creatorRequirements)}
                key={`${brief.id}-creator-requirements`}
                readOnly
              />
            </ListDetail>
            {brief.inviteOnly ? (
              <CheckboxDisplay className="creator-brief-view__invite-only" text="Invite to Apply Only" />
            ) : (
              <></>
            )}
            <ListDetail
              text="In-Shop Visit Required"
              labelClassName="creator-brief-view__heading"
              className="creator-brief-view__detail"
              hide={!Boolean(brief.states?.length)}
              showBottomSeparator
            >
              {brief.states?.map((state) => state.code).join(', ') || 'N/A'}
            </ListDetail>

            <ListDetail
              text="Content Guidelines"
              labelClassName="creator-brief-view__heading"
              className="creator-brief-view__detail"
              hide={isEqual(RICHTEXT_DEFAULT_VALUE, JSON.parse(brief.contentGuidelines))}
            >
              <Richtext value={JSON.parse(brief.contentGuidelines)} key={`${brief.id}-content-guidelines`} readOnly />
            </ListDetail>
            <ListDetail
              text="Dos & Don’ts"
              labelClassName="creator-brief-view__heading"
              className="creator-brief-view__detail"
              hide={isEqual(RICHTEXT_DEFAULT_VALUE, JSON.parse(brief.dosAndDonts))}
            >
              <Richtext value={JSON.parse(brief.dosAndDonts)} key={`${brief.id}-dos-donts`} readOnly />
            </ListDetail>
            <ListDetail
              text="Value Propositions"
              labelClassName="creator-brief-view__heading"
              className="creator-brief-view__detail"
              hide={isEqual(RICHTEXT_DEFAULT_VALUE, JSON.parse(brief.valuePropositions))}
            >
              <Richtext value={JSON.parse(brief.valuePropositions)} key={`${brief.id}-value-propositions`} readOnly />
            </ListDetail>
          </DefinitionList>

          <Box className="creator-brief-view__modules">
            {brief.modules?.map((module) => (
              <BriefModule module={module} key={module.id} />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CreatorBriefView;
